import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import ModalShell from "./ModalShell";
import { buttonClasses } from "./tailwindClassnames";
import { useVersionSwitch } from "./useVersionSwitch";

// GUNDI-5642: the hidden way into the new portal. Legacy is what everyone gets;
// the new version is reached by clicking the Gundi logo five times in quick
// succession and confirming the modal. New code (not a frozen import) mounted
// by the legacy nav, so it may use the CURRENT shared components.
//
// Deliberately not an accessible control: no cursor change, no hover state, no
// role, no keyboard path. It is an unadvertised gesture for a short-lived
// trial, not a feature — a labelled button is exactly what product asked NOT
// to have. Removed at sunset together with src/legacy/.
const CLICKS_TO_UNLOCK = 5;
// Clicks must land within five seconds of each other, so stray logo clicks
// minutes apart never add up to an unlock.
const CLICK_WINDOW_MS = 5000;
// Desktop only (product call). The nav logo this wraps renders at every
// breakpoint — only the nav's link list is hidden below md — so the gesture has
// to check the viewport itself rather than rely on where it is mounted.
// Tailwind's md breakpoint.
const DESKTOP_MIN_WIDTH = 768;

const onDesktop = () => {
    // No matchMedia (jsdom without a stub, very old browsers): fall back to the
    // viewport width rather than disabling the gesture outright.
    try {
        if (typeof window.matchMedia === "function") {
            return window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches;
        }
    } catch {
        // Fall through to the width check.
    }
    return window.innerWidth >= DESKTOP_MIN_WIDTH;
};

export default function NewVersionUnlock({ children }: { children: React.ReactNode }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { switchTo, switching } = useVersionSwitch();
    // Refs, not state: the count must not re-render the nav on every click.
    const clicks = useRef(0);
    const lastClickAt = useRef(0);

    const handleClick = () => {
        if (!onDesktop()) return;
        const now = Date.now();
        clicks.current = now - lastClickAt.current > CLICK_WINDOW_MS ? 1 : clicks.current + 1;
        lastClickAt.current = now;
        if (clicks.current < CLICKS_TO_UNLOCK) return;
        // Spend the count: cancelling the modal means five fresh clicks.
        clicks.current = 0;
        setOpen(true);
    };

    return (
        <>
            <span data-testid="NewVersionUnlock-span" onClick={handleClick}>
                {children}
            </span>
            {/* Portalled to <body>: the legacy nav wrapper carries a transform
                (its show/hide slide), which makes it the containing block for
                position: fixed — the modal would otherwise be trapped inside
                the nav strip instead of centred over the page. */}
            {open && createPortal(
                <ModalShell
                    title={t("Use the new version of Gundi?")}
                    onClose={() => setOpen(false)}
                    maxWidthClass="max-w-lg"
                    footer={
                        <>
                            <button
                                type="button"
                                data-testid="NewVersionUnlock-confirm-button"
                                disabled={switching}
                                onClick={() => switchTo("new")}
                                className={buttonClasses.primary}
                            >
                                {t("Switch to the new version")}
                            </button>
                            <button
                                type="button"
                                data-testid="NewVersionUnlock-cancel-button"
                                disabled={switching}
                                onClick={() => setOpen(false)}
                                className={buttonClasses.cancel}
                            >
                                {t("Cancel")}
                            </button>
                        </>
                    }
                >
                    <p className="text-base font-normal text-off-black">
                        {t("You'll switch to the redesigned portal. You can switch back at any time using the link at the top of the page. This only lasts until you log out or close the tab.")}
                    </p>
                </ModalShell>,
                document.body,
            )}
        </>
    );
}
